/**
 * @file: OrderRoomSnapshotCard.tsx
 *
 * Compact order card shown inside an order-internal thread row: canonical
 * vehicle name + status chip, with service advisor / mileage / in-shop
 * duration on a muted sub-line. Fed by the room's denormalized
 * `orderSnapshot` (kept fresh by the `room-info-updated` event), so it
 * renders without loading the order. The vehicle brand logo is the row's
 * avatar (not repeated here). Presentational.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrderChatRoomSnapshot } from '@euler/model/chat';
import { ReceptionOrderStatus } from '@euler/model/enum/ReceptionOrderStatus';
import { FC, memo } from 'react';
import { View } from 'react-native';

/** Elapsed time since the order was opened, as `N天M时` (M时 under a day). */
function inShopDuration(orderDateIso: string): string {
  const diff = Date.now() - new Date(orderDateIso).getTime();
  if (!(diff > 0)) return '';
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  return days > 0 ? `${days}天${hours}时` : `${hours}时`;
}

const STATUS_LABEL: Record<ReceptionOrderStatus, string> = {
  [ReceptionOrderStatus.Pending]: '待处理',
  [ReceptionOrderStatus.InProgress]: '进行中',
  [ReceptionOrderStatus.Finished]: '已完成',
  [ReceptionOrderStatus.Cancelled]: '已取消',
};

// [bg, text] per status; tuned to read on both the light row and the
// selected (blue) row, where we dim the whole card instead.
const STATUS_COLOR: Record<ReceptionOrderStatus, [string, string]> = {
  [ReceptionOrderStatus.Pending]: ['#f3f4f6', '#6b7280'],
  [ReceptionOrderStatus.InProgress]: ['#dbeafe', '#2563eb'],
  [ReceptionOrderStatus.Finished]: ['#dcfce7', '#16a34a'],
  [ReceptionOrderStatus.Cancelled]: ['#fee2e2', '#dc2626'],
};

export const OrderRoomSnapshotCard: FC<{
  snapshot: OrderChatRoomSnapshot;
  /** Row is selected (solid blue) — render the card light-on-blue. */
  selected?: boolean;
}> = memo(({ snapshot, selected }) => {
  const { rpx } = useResponsiveContext();
  const [chipBg, chipText] = STATUS_COLOR[snapshot.status];
  const nameColor = selected ? '#ffffff' : '#374151';
  const subColor = selected ? 'rgba(255,255,255,0.8)' : '#9ca3af';

  const duration = inShopDuration(snapshot.orderDate);
  const subParts = [
    snapshot.serviceAgentName ? `顾问 ${snapshot.serviceAgentName}` : null,
    snapshot.mileage ? `${snapshot.mileage}公里` : null,
    duration ? `在厂 ${duration}` : null,
  ].filter(Boolean);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: rpx(4),
      }}
    >
      <View style={{ flex: 1 }}>
        <Typography.Text size={rpx(12)} color={nameColor} numberOfLines={1}>
          {snapshot.vehicleName || snapshot.vehicleModel}
        </Typography.Text>
        {subParts.length > 0 ? (
          <Typography.Text size={rpx(11)} color={subColor} numberOfLines={1}>
            {subParts.join(' · ')}
          </Typography.Text>
        ) : null}
      </View>
      <View
        style={{
          marginLeft: rpx(6),
          paddingVertical: rpx(1),
          paddingHorizontal: rpx(6),
          borderRadius: rpx(4),
          backgroundColor: selected ? 'rgba(255,255,255,0.25)' : chipBg,
        }}
      >
        <Typography.Text size={rpx(11)} color={selected ? '#ffffff' : chipText}>
          {STATUS_LABEL[snapshot.status]}
        </Typography.Text>
      </View>
    </View>
  );
});
